import {Divider, Menu, MenuItem, Stack, Typography, LinearProgress} from '@mui/material';

import {dateReader, isValidDate, kilosToTons} from 'helpers/UnitsFormatHandler';

import useParseURL from 'hooks/useParseURL';
import {HarvestDemandPutSpeciesEnum, RecommendHarvestsGetSortByEnum, type HarvestRecommendation} from 'client';
import {Loading} from 'components/Loading';
import useGetHarvestRecommendations from 'hooks/useGetHarvestRecommendations';
import HarvestRecommendationCard from 'components/InputMenu/HarvestRecommendationCard';
import {useGetCageGrowthForecast, useGetPlanFishGroupProperties} from 'hooks/swr';
import {dequal} from 'dequal/lite';
import {useTranslation} from 'react-i18next';
import {type Dispatch, useState} from 'react';
import {ChipButton} from 'components/Generic/Buttons/ChipButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {MultiSelectChip} from 'components/Shared/MultiSelectChip';
import {actionTypeEnum, type IHarvest, type IHarvestAction} from './Harvest/reducer';
import {useShallow} from 'zustand/react/shallow';
import {createUseHarvestRecommendationsState} from './CageHarvestRecommendationsState';

const sortingOptions: RecommendHarvestsGetSortByEnum[] = [
  RecommendHarvestsGetSortByEnum.Weight,
  RecommendHarvestsGetSortByEnum.Score,
];

const speciesOptions: HarvestDemandPutSpeciesEnum[] = [
  HarvestDemandPutSpeciesEnum.Salmon,
  HarvestDemandPutSpeciesEnum.Trout,
];

export function CageHarvestRecommendations({state, dispatch}: {state: IHarvest; dispatch: Dispatch<IHarvestAction>}) {
  const {planId} = useParseURL();
  const {t} = useTranslation('common');

  const useHarvestRecommendationsState = createUseHarvestRecommendationsState(planId);
  const {
    sorting: filteredSorting,
    species: filteredSpecies,
    excludedProperties: filteredExcludedProperties,
    setState: setHarvestRecommendationsState,
  } = useHarvestRecommendationsState(
    useShallow((state) => ({
      sorting: state.sorting,
      species: state.species,
      excludedProperties: state.excludedProperties,
      setState: state.setState,
    })),
  );

  const [sortBy, setSortBy] = useState<RecommendHarvestsGetSortByEnum | undefined>(filteredSorting);
  const [species, setSpecies] = useState<HarvestDemandPutSpeciesEnum | undefined>(filteredSpecies);

  const {data: fishGroupProperties} = useGetPlanFishGroupProperties({resourceId: planId});
  const filterFishGroupProperties = fishGroupProperties?.map((property) => property.name) ?? [];
  const [selectedFishGroupProperties, setSelectedFishGroupProperties] = useState<string[] | undefined>(
    filteredExcludedProperties,
  );
  const hasFishGroupProperties = filterFishGroupProperties.length > 0;

  const biomassRecommendationQuery = state.biomass === 0 || isNaN(state.biomass) ? undefined : state.biomass;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const dateRecommendationQuery = state.date === null ? undefined : state.date;
  const isDateValid = dateRecommendationQuery && isValidDate(dateRecommendationQuery);
  const {isLoading, recommendedHarvests} = useGetHarvestRecommendations({
    planId: isDateValid ? planId : undefined,
    siteId: state.siteId,
    date: dateRecommendationQuery,
    biomass: biomassRecommendationQuery,
    sortBy,
    excludeWithProperties: getSelectedPropertyIds(selectedFishGroupProperties ?? [], fishGroupProperties),
  });

  const {data: growthByCage} = useGetCageGrowthForecast({resourceId: planId});
  // Union of species recommended from the API and LocalStorage filter species
  const uniqueSpeciesCount = new Set([
    ...(recommendedHarvests?.map((recommendation) => recommendation.species) ?? []),
    ...(species ? [species] : []),
  ]).size;
  const hasMoreThanTwoSpecies = uniqueSpeciesCount > 1;
  const noHarvestRecommendation = recommendedHarvests && recommendedHarvests.length === 0;
  return (
    <>
      <Stack p={0} direction={'column'} spacing={1}>
        <Stack p={0} direction={'row'} alignItems="center" justifyContent="space-between">
          <Typography>{t('recommendations')}</Typography>
          <Stack direction={'row'} spacing={1}>
            {hasMoreThanTwoSpecies && (
              <ChipMenu
                value={species}
                setValue={(_species) => {
                  if (species === _species) {
                    setSpecies(undefined);
                  } else {
                    setHarvestRecommendationsState('species', _species);
                    setSpecies(_species as HarvestDemandPutSpeciesEnum);
                  }
                }}
                values={speciesOptions}
              />
            )}
            <ChipMenu
              value={sortBy}
              setValue={(sortBy) => {
                setHarvestRecommendationsState('sorting', sortBy);
                setSortBy(sortBy as RecommendHarvestsGetSortByEnum);
              }}
              values={sortingOptions}
            />
          </Stack>
        </Stack>
        {hasFishGroupProperties && (
          <Stack direction={'row'} mt={0} justifyContent={'right'}>
            <MultiSelectChip
              label={t('excludeFishGroupProperties')}
              options={filterFishGroupProperties}
              selection={selectedFishGroupProperties ?? []}
              setSelection={(selection) => {
                setHarvestRecommendationsState('excludedProperties', selection);
                setSelectedFishGroupProperties(selection);
              }}
            />
          </Stack>
        )}
      </Stack>
      {isLoading ? <LinearProgress /> : <Divider />}
      <Stack spacing={0.5}>
        {recommendedHarvests && recommendedHarvests.length > 0 ? (
          recommendedHarvests
            .filter((recommendation) => (species ? recommendation.species === species : true))
            .map((harvestRecommendation) => (
              <HarvestRecommendationCard
                key={`${harvestRecommendation.count}-${harvestRecommendation.weight}`}
                harvest={harvestRecommendation}
                growthByCage={growthByCage}
                onSelection={() => {
                  dispatch({type: actionTypeEnum.setRecommendation, harvestRecommendation});
                }}
                isSelected={isSelected(harvestRecommendation, state)}
                hasMoreThanTwoSpecies={hasMoreThanTwoSpecies}
              />
            ))
        ) : (
          <>
            {(!recommendedHarvests || isLoading) && <Loading isFullHeight={false} />}
            {noHarvestRecommendation && <Typography>{t('noHarvestRecommendationAvailable')}</Typography>}
          </>
        )}
      </Stack>
    </>
  );
}

const ChipMenu = ({
  value,
  setValue,
  values,
}: {
  value?: RecommendHarvestsGetSortByEnum | HarvestDemandPutSpeciesEnum;
  setValue: (value: RecommendHarvestsGetSortByEnum | HarvestDemandPutSpeciesEnum) => void;
  values: RecommendHarvestsGetSortByEnum[] | HarvestDemandPutSpeciesEnum[];
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const {t} = useTranslation('common');

  return (
    <>
      <ChipButton
        aria-haspopup="true"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        size="small"
        color="inherit"
      >
        {value ? t(value) : t('all')}
      </ChipButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} sx={{zIndex: 3000}}>
        {values.map((_value) => (
          <MenuItem
            key={_value}
            onClick={() => {
              setValue(_value);
              handleClose();
            }}
            selected={_value === value}
          >
            {t(_value)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const isSelected = (harvestRecommendation: HarvestRecommendation, state: IHarvest) => {
  return dequal(
    {
      biomass: Math.round(kilosToTons(harvestRecommendation.biomass)),
      date: dateReader(harvestRecommendation.date),
      cageId: harvestRecommendation.cageId,
      fullHarvest: harvestRecommendation.fullHarvest,
      siteId: harvestRecommendation.siteId,
    },
    {
      biomass: Math.round(state.biomass),
      date: state.date,
      cageId: state.cageId,
      fullHarvest: state.fullHarvest,
      siteId: state.siteId,
    },
  );
};

const getSelectedPropertyIds = (
  selectedNames: string[],
  fishGroupProperties: Array<{name: string; id: string}> | undefined,
): string[] => {
  if (!fishGroupProperties) {
    return [];
  }
  return fishGroupProperties.filter((property) => selectedNames.includes(property.name)).map((property) => property.id);
};
